import { ApplicationError } from "../../models/errors/application-error";
import IDepartmentService from "./department-service-interface";
import { ApplicationDepartment } from "../../models/organisation/application-department";
import { ApplicationSubDepartmentList } from "../../models/organisation/application-sub-department-list";
import client from "../../external/open-api";
import { ApplicationSubDepartment } from "../../models/organisation/application-sub-department";
import { ApplicationDepartmentList } from "../../models/organisation/application-department-list";
import BaseService from "../base-service";
import { ApplicationOrderTemplate } from "../../models/organisation/application-order-template";
import { mapStringToEnum } from "../../helpers/enum-helper";
import { ApplicationProductSelectionMode } from "../../models/organisation/application-product-selection-mode";
import { components } from "../../external/schema/order-link-api-schema";
import { ApplicationOrderingMode } from "../../models/organisation/application-ordering-mode";

export default class DepartmentService extends BaseService implements IDepartmentService {
  async getDepartmentById(
    departmentId: number
  ): Promise<ApplicationDepartment> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/department/{departmentId}",
      {
        params: {
          path: { version: "1", departmentId }, // Path parameter
        },
      }
    ));

    if (!data?.data || error || !data.success) {
      throw new ApplicationError(
        `Failed to fetch department with ID ${departmentId}`
      );
    }
    const subDepartments: ApplicationSubDepartment[] =
      data.data.subDepartments?.map(this.mapToSubDepartment) || [];
    return {
      id: data.data.id ?? 0,
      name: data.data.name ?? "",
      active: data.data.active ?? false,
      canDelete: data.data.canDelete ?? false,
      orderTemplate: mapStringToEnum(ApplicationOrderTemplate, data.data.orderTemplate ?? "PurchaseOrder"),
      orderingMode: mapStringToEnum(ApplicationOrderingMode, data.data.orderingMode ?? "AllProducts"),
      subDepartments:
        subDepartments.length > 0 ? subDepartments : undefined,

    };
  }

  async updateDepartment(
    department: ApplicationDepartment
  ): Promise<ApplicationDepartment> {
    const { data, error } = await this.withRetry(() => client.PUT(
      "/v{version}/api/department/{departmentId}",
      {
        params: {
          path: { version: "1", departmentId: department.id }, // Path parameter
        },
        body: {
          ...department,
        },
      }
    ));

    if (!data?.data || error || !data.success) {
      throw new ApplicationError(
        `Failed to update department with ID ${department.id}`
      );
    }
    return data.data as unknown as ApplicationDepartment;
  }

  async getSubDepartments(
    departmentId: number,
    currentPosition: number,
    fetchNext: number,
    fetchAll: boolean
  ): Promise<ApplicationSubDepartmentList> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/department/{departmentId}/subdepartments",
      {
        params: {
          path: { version: "1", departmentId },
          query: {
            "Paging.CurrentPosition": currentPosition,
            "Paging.FetchNext": fetchNext,
            "Paging.FetchAll": fetchAll,
          },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to fetch sub department");
    }

    var subDepartments =
      data.data?.subDepartments?.map(this.mapToSubDepartment) || [];

    return {
      subDepartments: subDepartments,
      count: (data.data?.count ?? 0) == 0 ? -1 : data.data.count!,
      currentPosition: data.data?.currentPosition || 0,
    };
  }

  async getSubDepartmentById(subDepartmentId: number): Promise<ApplicationSubDepartment> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/department/subdepartments/{subDepartmentId}",
      {
        params: {
          path: { version: "1", subDepartmentId }, // Path parameter
        },
      }
    ));

    if (!data?.data || error || !data.success) {
      throw new ApplicationError(
        `Failed to fetch sub department with ID ${subDepartmentId}`
      );
    }

    return this.mapToSubDepartment(data.data);
  }

  async get(currentPosition: number, fetchNext: number, fetchAll: boolean): Promise<ApplicationDepartmentList> {
    const { data, error } = await this.withRetry(() => client.GET("/v{version}/api/department", {
      params: {
        path: { version: "1" },
        query: {
          "Paging.CurrentPosition": currentPosition,
          "Paging.FetchNext": fetchNext,
          "Paging.FetchAll": fetchAll,
        },
      },
    }));

    if (!data?.data || error || !data.success) {
      throw new ApplicationError("Failed to fetch departments");
    }

    return {
      count: data.data.count ?? -1,
      currentPosition: data.data.currentPosition ?? 0,
      departments: data.data.departments?.map((d) => ({
        id: d.id ?? 0,
        name: d.name ?? "",
        active: d.active ?? false,
        canDelete: d.canDelete ?? false,
        orderTemplate: mapStringToEnum(ApplicationOrderTemplate, d.orderTemplate ?? "PurchaseOrder"),
        orderingMode: mapStringToEnum(ApplicationOrderingMode, d.orderingMode ?? "AllProducts")
      })) || [],
      loading: false
    }
  }
  async deleteSubDepartment(subDepartmentId: number): Promise<void> {
    const { data, error } = await this.withRetry(() => client.DELETE(
      `/v{version}/api/department/subdepartments/{subDepartmentId}`,
      {
        params: {
          path: { version: "1", subDepartmentId },
        },
      }
    ));

    if (error || !data?.success) {
      throw new ApplicationError(
        `Failed to delete sub department with ID ${subDepartmentId}`
      );
    }
  }
  async deleteDepartment(departmentId: number): Promise<void> {
    const { data, error } = await this.withRetry(() => client.DELETE(
      `/v{version}/api/department/{departmentId}`,
      {
        params: {
          path: { version: "1", departmentId },
        },
      }
    ));

    if (error || !data?.success) {
      throw new ApplicationError(
        `Failed to delete department with ID ${departmentId}`
      );
    }
  }
  async createDepartment(
    name: string,
    productSelectionMode: string,
    orderTemplate: ApplicationOrderTemplate
  ): Promise<ApplicationDepartment> {
    const { data, error } = await this.withRetry(() => client.POST("/v{version}/api/department", {
      params: {
        path: { version: "1" },
      },
      body: {
        id: 0,
        name,
        productSelectionMode,
        orderTemplate,
        active: false,
        departmentCategories: [],
      },
    }));

    if (!data?.data || error || !data.success) {
      throw new ApplicationError("Failed to create department");
    }

    return data.data as ApplicationDepartment;
  }

  private mapToSubDepartment(
    subDepartment: components["schemas"]["SubDepartmentDTO"]
  ): ApplicationSubDepartment {
    return {
      id: subDepartment.id ?? 0,
      departmentId: subDepartment.departmentId ?? 0,
      name: subDepartment.name ?? "",
      description: subDepartment.description ?? "",
      productSelectionMode: mapStringToEnum(ApplicationProductSelectionMode, subDepartment.productSelectionMode ?? ApplicationProductSelectionMode.AddOn),
      active: subDepartment.active ?? false,
      canDelete: subDepartment.canDelete ?? false,
    };
  }
}